<template>
	<v-app>
		<v-main>
			<v-container class="fill-height" fluid :style="loginBgStyle">
				<v-row align="center" justify="center">
					<v-col cols="12" sm="6" md="4" lg="3">
						<v-card class="elevation-12 rounded-lg">
							<v-card-title class="primary white--text justify-center py-6">
								<div class="text-center">
									<img v-if="portalLogo" :src="portalLogo" alt="" style="max-height: 60px; margin-bottom: 8px;">
									<v-icon v-else dark x-large class="mb-2">mdi-shield-account</v-icon>
									<div class="text-h5 font-weight-bold">{{ portalName }}</div>
								</div>
							</v-card-title>
							<v-card-text v-if="step !== 'credentials'" class="pa-6">
								<portal-two-factor-step
									:mode="step"
									:email="email"
									:qrcode="twofa.qrcode"
									:secret="twofa.secret"
									:issuer="twofa.issuer"
									:loading="loading"
									:error="error"
									@submit="verifyOtp"
									@back="backToCredentials"
								></portal-two-factor-step>
							</v-card-text>
							<v-card-text v-else class="pa-6">
								<v-alert v-if="info && !error" type="info" dense outlined class="mb-4">
									{{ info }}
								</v-alert>
								<v-alert v-if="error" type="error" dense outlined class="mb-4">
									{{ error }}
								</v-alert>
								<v-form ref="form" @submit.prevent="login" lazy-validation>
									<v-text-field
										v-model="email"
										label="Email"
										prepend-icon="mdi-email"
										type="email"
										:rules="emailRules"
										outlined
										dense
										class="mb-2"
									></v-text-field>
									<v-text-field
										v-model="password"
										label="Password"
										prepend-icon="mdi-lock"
										:type="showPassword ? 'text' : 'password'"
										:append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
										@click:append="showPassword = !showPassword"
										:rules="[v => !!v || 'Password obbligatoria']"
										outlined
										dense
									></v-text-field>
									<v-btn
										type="submit"
										color="primary"
										block
										large
										:loading="loading"
										:disabled="loading"
										class="mt-4"
									>
										Accedi
									</v-btn>
								</v-form>
							</v-card-text>
						</v-card>
					</v-col>
				</v-row>
			</v-container>
		</v-main>
	</v-app>
</template>

<script>
import PortalTwoFactorStep from './PortalTwoFactorStep'
import { firstValidationError } from './portalPasswordPolicy'

const EMPTY_TWOFA = { qrcode: '', secret: '', issuer: '' }

export default {
	name: 'PortalLogin',
	components: {
		PortalTwoFactorStep
	},
	data() {
		return {
			email: '',
			password: '',
			showPassword: false,
			loading: false,
			error: null,
			info: null,
			emailRules: [
				v => !!v || 'Email obbligatoria',
				v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'Indirizzo email non valido'
			],
			// 'credentials' → 'otp' (2FA gia' configurata) o 'enroll' (QR da scansionare)
			step: 'credentials',
			twofa: Object.assign({}, EMPTY_TWOFA),
			portalLogo: null,
			portalName: 'Area Privata',
			portalColor: '#F8C11D'
		}
	},
	computed: {
		loginBgStyle() {
			return {
				background: '#ffffff',
				minHeight: '100vh'
			}
		}
	},
	created() {
		// Se autenticato con cambio password obbligatorio, vai subito a change-password.
		// Con un token in query no: è un accesso da impersonazione e la sessione da
		// guardare è quella che sta per essere aperta, non quella ancora in memoria.
		if (!this.$route.query.token && this.$store.getters.isPortalAuthenticated && this.$store.getters.portalForcePasswordChange) {
			this.$router.replace('/portal/change-password')
			return
		}
		// Sessione chiusa dal server (2FA resa obbligatoria, reset della 2FA)
		if (this.$store.state.portal.sessionExpired) {
			this.info = 'La sessione è scaduta: accedi di nuovo.'
			this.$store.commit('PORTAL_CLEAR_SESSION_EXPIRED')
		}
		this.setupBaseUrl()
		// Pulisci il token admin per il contesto portale
		delete this.axios.defaults.headers['Authorization']
		this.loadBranding()
		if (this.$route.query.token) {
			this.autoLogin(this.$route.query.token)
		}
	},
	methods: {
		async loadBranding() {
			// Usa cache localStorage come fallback immediato
			const cached = localStorage.getItem('portal-branding')
			if (cached) {
				try {
					const d = JSON.parse(cached)
					if (d.portal_color) {
						this.portalColor = d.portal_color
						this.$vuetify.theme.themes.light.primary = d.portal_color
						this.$vuetify.theme.themes.dark.primary = d.portal_color
					}
					if (d.portal_logo) this.portalLogo = d.portal_logo
					if (d.portal_company_name) this.portalName = d.portal_company_name
				} catch (e) { /* cache corrotta, ignora */ }
			}
			try {
				const resp = await this.axios.get('portal/portal-branding')
				if (resp.data.success) {
					const d = resp.data.data
					localStorage.setItem('portal-branding', JSON.stringify(d))
					if (d.portal_color) {
						this.portalColor = d.portal_color
						this.$vuetify.theme.themes.light.primary = d.portal_color
						this.$vuetify.theme.themes.dark.primary = d.portal_color
					}
					if (d.portal_logo) {
						this.portalLogo = d.portal_logo
					}
					if (d.portal_company_name) {
						this.portalName = d.portal_company_name
					}
				}
			} catch (e) {
				// Branding non disponibile, usa cache o defaults
			}
		},
		// Riporta axios alla sessione precedente dopo un token rifiutato. Non basta
		// rimettere gli header salvati: created() cancella l'Authorization prima di
		// arrivare qui (serve a non far uscire il token dell'area admin verso il
		// portale), quindi lo snapshot ne e' gia' privo. PORTAL_RESTORE_SESSION lo
		// ricostruisce da localStorage, dove la sessione precedente e' ancora
		// intatta, e non fa nulla se quella sessione non c'era.
		ripristinaSessione(headers) {
			this.axios.defaults.headers = headers
			this.$store.dispatch('PORTAL_RESTORE_SESSION')
		},
		async autoLogin(token) {
			this.loading = true
			this.error = null

			// La sessione in corso non va toccata finché il token nuovo non è validato:
			// un link con un token scaduto o manomesso non deve poter buttare fuori chi
			// sta già usando il portale. Si spostano quindi solo gli header di axios,
			// ripristinandoli se la validazione fallisce.
			// X-Portal-Company va tolto a parte: è globale e, restando quello
			// dell'utente precedente, verrebbe inviato insieme al token nuovo.
			const headersPrecedenti = Object.assign({}, this.axios.defaults.headers)
			delete this.axios.defaults.headers['X-Portal-Company']
			this.axios.defaults.headers['Authorization'] = 'Bearer ' + token

			try {
				const resp = await this.axios.get('portal/profile')
				if (resp.data.success) {
					const companies = resp.data.data.companies || []
					// Solo ora, a token validato, la sessione precedente viene chiusa.
					this.$store.commit('PORTAL_CLEAR_SESSION')
					this.$store.commit('PORTAL_SUCCESS', {
						token: token,
						user: resp.data.data.user,
						companies: companies,
						force_password_change: false
					})
					// replace e non push: l'indirizzo col token non resta nella
					// cronologia, dove il tasto Indietro lo rieseguirebbe.
					if (companies.length > 1) {
						this.$router.replace('/portal/select-company')
					} else {
						if (companies.length === 1) {
							this.$store.dispatch('PORTAL_SELECT_COMPANY', companies[0].id)
						}
						this.$router.replace('/portal')
					}
				} else {
					this.error = 'Token non valido'
					this.ripristinaSessione(headersPrecedenti)
				}
			} catch (e) {
				this.error = 'Errore nell\'accesso automatico'
				this.ripristinaSessione(headersPrecedenti)
			} finally {
				this.loading = false
			}
		},
		setupBaseUrl() {
			let hostname = window.location.hostname

			// Priorità 1: query param ?fqdn= (da link email)
			if (this.$route.query.fqdn) {
				const fqdn = this.$route.query.fqdn
				localStorage.setItem('fqdn', fqdn)
				this.$store.dispatch('SET_FQDN')
				return
			}

			// Priorità 2: subdomain (es. stiip.givemehive.cloud -> stiip).
			// Esclude i subdomain riservati: su app.givemehive.cloud o localhost
			// si usa il fqdn già in localStorage (gestito da main.js SET_FQDN).
			const RESERVED = ['app', 'www', 'localhost']
			const candidate = hostname.split('.')[0]

			if (candidate && !RESERVED.includes(candidate)) {
				localStorage.setItem('fqdn', candidate)
				this.$store.dispatch('SET_FQDN')
			}
		},
		login() {
			if (!this.$refs.form.validate()) return
			this.sendCredentials()
		},
		verifyOtp(otp) {
			this.sendCredentials(otp)
		},
		backToCredentials() {
			this.step = 'credentials'
			this.twofa = Object.assign({}, EMPTY_TWOFA)
			this.password = ''
			this.error = null
		},
		// Come nel login GMH il server non tiene stato fra i passi: email e
		// password vengono rimandate insieme all'OTP. Restano solo nei dati di
		// questo componente e si azzerano a login riuscito o tornando indietro.
		async sendCredentials(otp) {
			// Un doppio invio senza codice durante la configurazione genererebbe
			// due secret in parallelo, e il QR mostrato potrebbe non valere piu'
			if (this.loading) return
			this.loading = true
			this.error = null
			this.info = null

			const payload = { email: this.email, password: this.password }
			if (otp) {
				payload.otp = otp
			}

			try {
				const result = await this.$store.dispatch('PORTAL_LOGIN', payload)

				if (result.success) {
					this.password = ''
					this.twofa = Object.assign({}, EMPTY_TWOFA)
					this.goAfterLogin(result.data)
				} else if (result.data === 'insert_otp') {
					this.step = 'otp'
				} else if (result.data && result.data.qrcode) {
					this.twofa = {
						qrcode: result.data.qrcode,
						secret: result.data.google2fa_secret,
						issuer: result.data.issuer || ''
					}
					this.step = 'enroll'
				} else {
					this.error = result.message || 'Credenziali non valide'
				}
			} catch (err) {
				const status = err.response ? err.response.status : null
				const data = err.response ? err.response.data : null
				const code = data ? data.error_code : null
				if (status === 429) {
					// Blocco per codici errati o limite di richieste: si resta sul
					// passo corrente, tornare indietro non servirebbe
					this.error = code === 'otp_throttled' ? data.message : 'Troppi tentativi. Riprova tra un minuto.'
				} else if (otp && code === 'invalid_otp') {
					// Si resta sul passo dell'OTP: il componente svuota il campo
					this.error = data.message
				} else {
					if (this.step !== 'credentials') {
						this.backToCredentials()
					}
					this.error = status === 422
						? firstValidationError(err, 'Dati non validi')
						: (data && data.message) || 'Errore di connessione. Riprova.'
				}
			} finally {
				this.loading = false
			}
		},
		goAfterLogin(data) {
			if (data.force_password_change) {
				this.$router.push('/portal/change-password')
			} else if (data.companies && data.companies.length > 1) {
				this.$router.push('/portal/select-company')
			} else {
				// 1 sola company: select automatico
				if (data.companies && data.companies.length === 1) {
					this.$store.dispatch('PORTAL_SELECT_COMPANY', data.companies[0].id)
				}
				this.$router.push('/portal')
			}
		}
	}
}
</script>

<style scoped>
.portal-login-bg {
	min-height: 100vh;
}
</style>
